import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DsButtonVariants } from '@levelaccess/design-system';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Api } from '../../../../../../../shared/constants/api';
import { WORKSPACES_SHEET_NAME, bulkUpload } from '../../../../../../../shared/constants/bulk-workbook';
import { MimeTypes } from '../../../../../../../shared/constants/mime-type';
import { $sortingOrder } from '../../../../../../../shared/constants/sort';
import { IValidateBulkUpload, IValidationErrors } from '../../../../../../../shared/interfaces/digital-property.interface';
import { IGlobalError } from '../../../../../../../shared/interfaces/error.interface';
import { ICreateBulkWorkspacesResponse } from '../../../../../../../shared/interfaces/workspace.interface';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { ICellConfig } from '../../../../../components/table/ngb-table/cells/base-cell/base-cell.component';
import {
  IDclConfig,
  ITableColumn,
  ITableConfig,
  ITableRow,
  SortEvent,
} from '../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { NgbTableUtilities } from '../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { A11yService } from '../../../../../services/a11y.service';
import { ErrorHandlerService } from '../../../../../services/error-handler.service';
import { ErrorMessageService } from '../../../../../services/error-message.service';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { TenantWorkspaceService } from '../../../../../services/tenant-workspace.service';
import { WorkspaceService } from '../../../../../services/workspace.service';
import { TranslateService } from '../../../../../translate/translate.service';
import { TenantRouteUtil } from '../../../tenant-route.util';
import { CommonUtility } from '../../../../../utility/common.utility';
import { NotificationService } from '../../../../../services/notification.service';
import { NotificationPosition } from '../../../../../models/notification.model';

enum tableColumns {
  issues = 'issues',
  rows = 'rows',
  sheet = 'sheet',
}

interface IErrors extends IValidationErrors {
  translatedMessage: string;
}

@Component({
  selector: 'app-create-bulk-ws-plus-dp',
  templateUrl: './create-bulk-ws-plus-dp.component.html',
  styleUrls: ['./create-bulk-ws-plus-dp.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateBulkWsPlusDpComponent implements OnDestroy, OnInit {
  private PAGE_SIZE: number = 10;
  private subscriptions: Subscription;
  private bulkUploadFile: File | undefined;
  public tenantDetailsUrl: string[];
  private sortOption$: BehaviorSubject<SortEvent>;

  public ACCEPTED_FILE_TYPES: string[] = [MimeTypes.XLSX];
  public FILE_UPLOADED_MESSAGE: string = 'label_workspaces_and_digital_properties_will_be_added';
  public ACCEPTED_FILE_ERROR: string = 'error_only_xlsx_file_allowed_for_upload';
  public FILE_DATA_ERROR: string = 'error_bulk_upload_failed_description';

  @Input() tenantId: string;
  @Input() modalMode: boolean = false;

  @Output() bulkCreated: EventEmitter<void> = new EventEmitter<void>();
  @Output() cancelled: EventEmitter<void> = new EventEmitter<void>();

  public Api: typeof Api;
  public saveError: IGlobalError;
  public formBulk: UntypedFormGroup;
  public fileValid: boolean;
  public fileName: string;
  public fileUploaded: boolean;
  public fileDropError: string;

  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public tableConfig: ITableConfig;
  public validateErrors$: BehaviorSubject<IErrors[]>;
  public tableData$: BehaviorSubject<ITableRow[]>;
  public page$: BehaviorSubject<number>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private workspaceService: WorkspaceService,
    private loadErrorHandler: LoadErrorHandler,
    private a11yService: A11yService,
    private errorMessageService: ErrorMessageService,
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private errorHandlerService: ErrorHandlerService,
    private tenantWorkspaceService: TenantWorkspaceService,
    private notificationService: NotificationService,
  ) {
    this.page$ = new BehaviorSubject<number>(1);
    this.subscriptions = new Subscription();
    this.Api = Api;
    this.buildForm();
    this.bulkUploadFile = undefined;
    this.fileValid = undefined;
    this.fileUploaded = undefined;
    this.fileDropError = undefined;
    this.tableData$ = new BehaviorSubject<ITableRow[]>([]);
    this.validateErrors$ = new BehaviorSubject<IErrors[]>([]);
    this.sortOption$ = new BehaviorSubject<SortEvent>({
      column: tableColumns.sheet,
      direction: $sortingOrder.all,
    });
  }

  private getTableConfig(): ITableConfig {
    const columns: Record<string, ITableColumn> = {
      [tableColumns.issues]: {
        translationKey: 'issues',
        sortingEnabled: true,
        styles: { minWidth: '80%', width: '80%' },
      },
      [tableColumns.sheet]: {
        translationKey: 'sheet',
        sortingEnabled: true,
        styles: { minWidth: '15%', width: '15%' },
      },
      [tableColumns.rows]: {
        translationKey: 'column_header_row',
        sortingEnabled: true,
        styles: { minWidth: '20%', width: '20%' },
      },
    };

    return {
      columns: columns,
      caption: this.translateService.instant('label_correct_fields_below'),

      singleSelect: {
        selectable: false,
        selectFirstRow: true,
      },
    };
  }

  public onSort(sort: SortEvent): void {
    this.sortOption$.next(sort);
    this.page$.next(1);
  }

  private getTableData(validateErrors: IErrors[]): ITableRow[] {
    const toTableRow = (error: IErrors): ITableRow => {
      const data: Record<string, IDclConfig<ICellConfig>> = {
        [tableColumns.issues]: NgbTableUtilities.textCell({
          text: error.translatedMessage,
        }),
        [tableColumns.sheet]: NgbTableUtilities.textCell({
          text: error.sheet,
        }),
        [tableColumns.rows]: NgbTableUtilities.textCell({
          text: error.lines.join(', '),
        }),
      };
      return { data };
    };

    if (this.sortOption$.getValue().direction === $sortingOrder.all) {
      return validateErrors.sort(this.defaultComparator.bind(this)).map(toTableRow);
    }
    return validateErrors.sort(this.comparatorByColumn.bind(this)).map(toTableRow);
  }

  private getWorksheetNumberValue(worksheet: string): number {
    return worksheet === WORKSPACES_SHEET_NAME ? -1 : 1;
  }

  private getRowNumberValue(rows?: number[]): number {
    return rows.length > 0 ? rows[0] : -1;
  }

  private comparatorByColumn(a: IErrors, b: IErrors): number {
    const { column: sortColumn, direction } = this.sortOption$.getValue();

    const multiplier: number = direction === $sortingOrder.asc ? 1 : -1;
    switch (sortColumn) {
      case tableColumns.sheet:
        return (this.getWorksheetNumberValue(a.sheet) - this.getWorksheetNumberValue(b.sheet)) * multiplier;
      case tableColumns.issues:
        return a.translatedMessage.localeCompare(b.translatedMessage) * multiplier;
      case tableColumns.rows:
        return (this.getRowNumberValue(a.lines) - this.getRowNumberValue(b.lines)) * multiplier;
      default:
        return 0;
    }
  }

  private defaultComparator(a: IErrors, b: IErrors): number {
    if (this.getWorksheetNumberValue(a.sheet) - this.getWorksheetNumberValue(b.sheet) !== 0) {
      return this.getWorksheetNumberValue(a.sheet) - this.getWorksheetNumberValue(b.sheet);
    }
    return this.getRowNumberValue(a.lines) - this.getRowNumberValue(b.lines);
  }

  private onValidateDigitalPropertyBulkUploadSuccess(result: IValidateBulkUpload): void {
    this.fileUploaded = true;
    this.fileValid = result.validate;

    if (SharedCommonUtility.notNullish(result.validate) && result.validate === false) {
      this.validateErrors$.next(
        result.validateErrors.map((e: IValidationErrors): IErrors => {
          return {
            ...e,
            translatedMessage: this.translateService.instant(e.message),
          };
        }),
      );
      this.tableConfig = this.getTableConfig();
    } else {
      this.validateErrors$.next([]);
    }
    this.changeDetectorRef.detectChanges();
  }

  private handleCreateResponse(response: ICreateBulkWorkspacesResponse, error?: HttpErrorResponse): void {
    if (this.modalMode) {
      if (SharedCommonUtility.isNullish(error)) {
        const toastMessage: string = this.translateService.instant('workspaces_added', [
          response.workspacesLoaded,
          response.digitalPropertiesLoaded,
        ]);
        this.notificationService.success(toastMessage, NotificationPosition.Toast);
        this.bulkCreated.emit();
      } else {
        this.handleError('error_bulk_workspace_create_failed', error);
      }
      return;
    }

    if (SharedCommonUtility.notNullish(error)) {
      this.tenantWorkspaceService.workspacesStatus = {
        ...response,
        error: 'error_bulk_workspace_create_failed',
      };
    } else {
      this.tenantWorkspaceService.workspacesStatus = {
        ...response,
      };
    }

    this.router
      .navigate([Api.admin, Api.organizations, Api.my, this.tenantId])
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  public createBulk(): void {
    if (SharedCommonUtility.isNullish(this.bulkUploadFile)) {
      return;
    }

    const bulkUploadFileForm: FormData = new FormData();
    bulkUploadFileForm.append(bulkUpload, this.bulkUploadFile, this.bulkUploadFile.name);
    bulkUploadFileForm.append('tenantId', this.tenantId);

    this.subscriptions.add(
      this.workspaceService.workspaceBulkUpload(bulkUploadFileForm).subscribe({
        next: this.handleCreateResponse.bind(this),
        error: (error: HttpErrorResponse) => {
          this.handleCreateResponse({ workspacesLoaded: 0, digitalPropertiesLoaded: 0 }, error);
        },
      }),
    );
  }

  public buildForm(): void {
    this.formBulk = this.formBuilder.group({
      [bulkUpload]: [null, []],
    });
  }

  public inputFile(file?: File): void {
    if (file) {
      this.bulkUploadFile = file;
      this.fileName = this.bulkUploadFile.name;
      this.fileUploaded = false;
    } else {
      this.bulkUploadFile = undefined;
      this.fileValid = undefined;
      this.fileName = undefined;
      this.fileUploaded = undefined;
    }
  }

  public onPageChange(page: number): void {
    this.page$.next(page);
  }

  public validate(): void {
    const bulkUploadFileForm: FormData = new FormData();
    bulkUploadFileForm.append(bulkUpload, this.bulkUploadFile, this.bulkUploadFile.name);
    bulkUploadFileForm.append('tenantId', this.tenantId);

    this.subscriptions.add(
      this.workspaceService.validateWorkspaceBulkUpload(bulkUploadFileForm).subscribe({
        next: this.onValidateDigitalPropertyBulkUploadSuccess.bind(this),
        error: (error: HttpErrorResponse) => {
          this.handleError('error_validation_failed', error);
        },
      }),
    );
  }

  private handleError(message: string, response: HttpErrorResponse): void {
    const errorMessage: string = this.errorMessageService.getGlobalErrorResponse(response);
    const appMessage: string = this.translateService.instant(message);

    this.a11yService.setMessage(appMessage + ': ' + errorMessage);
    this.loadErrorHandler.handleError(appMessage, response, CreateBulkWsPlusDpComponent.name);
    console.error(`[${CreateBulkWsPlusDpComponent.name}.handleError: `, appMessage, ']', response, errorMessage);

    this.saveError = response.error;

    this.changeDetectorRef.detectChanges();
  }

  public downloadTemplate(): void {
    if (this.modalMode) {
      const downloadUrl: string = this.workspaceService.getCreateBulkWorkspacesTemplateUrl();
      CommonUtility.navigateToExternalUrl(downloadUrl, true);
    } else {
      this.subscriptions.add(
        this.workspaceService.downloadCreateBulkWorkspacesTemplate().subscribe({
          error: (error: HttpErrorResponse) => {
            this.handleError('error_file_missing', error);
          },
        }),
      );
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    if (!this.tenantId) {
      this.tenantId = this.activatedRoute.snapshot.params[Api.tenantId];
    }

    if (!this.modalMode) {
      this.tenantDetailsUrl = [TenantRouteUtil.getTenantRoutePath(this.router.url), this.tenantId];
    }

    this.subscriptions.add(
      combineLatest([this.validateErrors$, this.sortOption$, this.page$])
        .pipe(
          map(([errors, sort, page]: [IErrors[], SortEvent, number]): ITableRow[] => {
            return this.getTableData(errors).slice((page - 1) * this.PAGE_SIZE, page * this.PAGE_SIZE);
          }),
        )
        .subscribe(this.tableData$),
    );
  }
}
